import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { IDocumentPartProps } from "./IDocumentPartProps";

import {
  processTreeViewItems,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent,
} from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import { fetchActionWorkflow } from "../../api/document/DocumentsPart";
import { ContextualMenuItemType, IContextualMenuItem } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { DocumentRoot } from "../../api/models/document-root";
import { DocumentPart } from "../../api/models";
import { WorkflowStatus } from "../../api/models/workflow-status";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { fetchExpandedNodeExternal } from "./DocumentPartTreeHelper";
import { useWindowSize } from "../../utils/helpers/useWindowSizeHook";
import { CustomTreeView } from "./CustomTreeView";

const clampNumber = (num, a, b) => Math.max(Math.min(num, Math.max(a, b)), Math.min(a, b));

export const Tooltip = ({
  positionY,
  children,
}: {
  positionY: number;
  positionX: number;
  children: ReactElement | string | (ReactElement | string)[];
}) => {
  const { width, height } = useWindowSize();

  const positionMinusY = height - positionY;
  const bottomOffset = clampNumber(positionMinusY + 30, 5, height - 5);
  const topOffset = clampNumber(positionY + 30, 5, height - 5);
  const isInBottonScreen = positionY > height / 2;
  const topBottomStyles = isInBottonScreen ? { bottom: bottomOffset } : { top: topOffset };

  return (
    <div
      className="tooltip"
      style={{
        position: "fixed",
        backgroundColor: "var(--color-lightgrey)",
        color: "black",
        borderRadius: "5px",
        border: "1px solid var(--color-black)",
        padding: "2px",
        opacity: 1,
        maxWidth: width - 10 + "px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        right: "50%",
        transform: "translateX(50%)",
        fontSize: "10px",
        ...topBottomStyles,
      }}
    >
      {children}
    </div>
  );
};

export interface DocumentPartTreeSelection {
  item: any;
  IdRoot: string;
  name: string;
  key: string;
  order: number;
  oidPath: string[];
  data: any;
}

export interface TreeState {
  rootId: string;
  treeItems: TreeViewDataItem[];
  selectState: string[];
  selectedId?: string;
  page: string;
}

export interface TreeViewDataItem {
  text: string;
  key: string;
  order: number;
  hasChildren: boolean;
  nodeType: "part" | "section" | "root" | "pagination";
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  items?: TreeViewDataItem[];
  data: any;
  driveItemId?: string;
  hasReadPermissions?: boolean;
  fatherMultilinked?: boolean;
  fatherKey?: string;
  multilinkedParents?: DocumentPart[];
}

/**
 * Ultis functions
 */
export function mapFormToDataItem(part: DocumentRoot): TreeViewDataItem {
  return {
    text: part.documentPart.name.replace(/\.docx$/, ""),
    key: part.documentPart.id,
    order: part.documentPart.order,
    hasChildren: part.documentPart.hasChildren,
    nodeType: "part",
    expanded: false,
    items: undefined,
    data: part,
    hasReadPermissions: part.hasReadPermissions,
  };
}

export const DocumentPartTree: React.FunctionComponent<IDocumentPartProps> = (props) => {
  const { rootParts } = props;
  const expandIcons: boolean = props.expandIcons == undefined;
  const [state, setState] = useState<TreeState>({
    rootId: rootParts[0].key,
    treeItems: rootParts,
    selectState: [],
    page: "outline",
  });
  const refreshArrayDocumentPartWithStatus = (
    newElement: DocumentPartWithStatus[],
    array: DocumentPartWithStatus[]
  ) => {
    array = array.map((element) => {
      const matchingElement = newElement?.find((newEl) => newEl.documentPartId === element.documentPartId);
      return matchingElement ? matchingElement : element;
    });
    return array;
  };

  const [disableActionWorkflow, setDisableActionWorkflow] = useState<boolean>(false);
  let workflowStatus: WorkflowStatus;
  function changeState(item: TreeViewDataItem, action: string) {
    setDisableActionWorkflow(true);
    props.setDocumentPartWithStatus((prevState) => {
      prevState.forEach((documentPartWithStatus) => {
        if (documentPartWithStatus.documentPartId === item.key) {
          workflowStatus = documentPartWithStatus.workflowStatus;
          documentPartWithStatus.workflowStatus = null;
        }
      });
      return prevState;
    });
    fetchActionWorkflow(state.rootId, item.key, action)
      .then((items) => {
        props.setDocumentPartWithStatus((prevState) => {
          return refreshArrayDocumentPartWithStatus(items, prevState);
        });
        setDisableActionWorkflow(false);
      })
      .catch(() => restoreWorkflowStatus(item.key, workflowStatus));
  }

  function getStyle(action: string) {
    let style;
    switch (action.toLowerCase()) {
      case "approve":
        style = { fontWeight: "bold", color: "#93c33a" };
        break;
      case "reject":
        style = { fontWeight: "bold", color: "red" };
        break;
      default:
        style = { fontWeight: "bold", color: "black" };
        break;
    }
    return style;
  }
  function restoreWorkflowStatus(id: string, workflowStatus: WorkflowStatus) {
    props.setDocumentPartWithStatus((prevState) => {
      prevState.forEach((documentPartWithStatus) => {
        if (documentPartWithStatus.documentPartId === id) {
          documentPartWithStatus.workflowStatus = workflowStatus;
        }
      });
      return prevState;
    });
    setDisableActionWorkflow(false);
  }

  function certificationOptions(item: TreeViewDataItem) {
    let options: IContextualMenuItem[] = [];
    let items: IContextualMenuItem[] = [];
    let element: string[] = getWorkflowStatus(item.key, props.documentPartWithStatus)?.actions;
    let workflowStatus: WorkflowStatus = getWorkflowStatus(item.key, props.documentPartWithStatus);

    element?.forEach((action) => {
      if (!disableActionWorkflow) {
        items.push({
          key: action,
          text: formatMessage("cof.base." + action.toLowerCase()),
          onClick: () => {
            changeState(item, action);
          },
          style: getStyle(action),
        });
      }
    });
    if (items?.length > 0) {
      options.push({
        key: "menu",
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          topDivider: true,
          bottomDivider: true,
          title: getUserUpdInfo(workflowStatus),
          items: items,
        },
      });
    }
    return {
      items: options,
      directionalHintFixed: true,
    };
  }

  function getUserUpdInfo(workflowStatus: WorkflowStatus) {
    if (workflowStatus.workflowStatusCode === "50" || workflowStatus.workflowStatusCode === "30") {
      return formatMessage("cof.base.submitted.by") + " " + workflowStatus?.userUpd?.split("@")[0];
    }
  }

  useEffect(() => {
    setState({
      rootId: rootParts[0].key,
      treeItems: rootParts,
      selectState: [],
      page: "outline",
    });
  }, [rootParts]);

  const treePath = (items: TreeViewDataItem[], indexes: number[]): TreeViewDataItem[] => {
    const item = items[indexes[0]];
    if (item?.items && item.items.length > 0) return [item, ...treePath(item.items, indexes.slice(1))];
    return [item];
  };

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    fetchExpandedNodeExternal(
      event.itemHierarchicalIndex,
      false,
      0,
      false,
      state.treeItems,
      props.elementToUpdate
    ).then((treeItems) => setState((s) => ({ ...s, treeItems })));
  };

  const selectItem = (event: TreeViewItemClickEvent) => {
    if (event.item.nodeType !== "section" && event.item.nodeType !== "pagination") {
      setState((s) => ({ ...s, selectState: [event.itemHierarchicalIndex], selectedId: event.item.key }));
      if (props.onItemClick) {
        props.onItemClick({
          item: event.item,
          IdRoot: event.item.IdRoot,
          name: event.item.data.name,
          order: event.item.order,
          key: event.item.key,
          data: event.item,
          oidPath: treePath(
            state.treeItems,
            event.itemHierarchicalIndex.split("_").map((value) => Number(value))
          ).map((item) => (item ? item.key : "0")),
        });
      }
    }
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    switch (event.syntheticEvent["detail"]) {
      case 1: // single click
        selectItem(event);
        break;
      case 2: // double click
        openDocOnDoubleClick(event.item);
        break;
    }
    event.nativeEvent.preventDefault();
  };

  function openDocOnDoubleClick(item: TreeViewDataItem) {
    if (item.hasReadPermissions !== false && item.nodeType !== "pagination") {
      props.openDocument(item.key);
    }
  }

  const fetchExpandedNode = useCallback(
    (id: string, isReload: boolean, page: number) => {
      fetchExpandedNodeExternal(id, isReload, page, false, state.treeItems, props.elementToUpdate).then((treeItems) =>
        setState((s) => ({ ...s, treeItems }))
      );
    },
    [props.elementToUpdate, state]
  );

  return (
    <>
      {props.isLibraryRibbon ? (
        <div
          style={{
            borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
            width: "100%",
            height: "3px",
          }}
        />
      ) : null}
      <CustomTreeView
        data={processTreeViewItems(state.treeItems, { select: state.selectState })}
        expandIcons={expandIcons}
        onExpandChange={onExpandChange}
        selectedItemId={props.selectedPartId}
        treeItemRowProps={{
          isDraggable: state.page === "dragedrop",
          getIconButtonMenuProps: props.getIconButtonMenuProps,
          fetchExpandedNode: fetchExpandedNode,
          isLinkExisting: props.isLinkExisting,
          certificationOptions: certificationOptions,
          isOutlineRibbon: props.isOutlineRibbon,
          isOpenOutlineRibbon: props.isOpenOutlineRibbon,
          azureDefaultSite: props.azureDefaultSite,
          documentPartWhithStatus: props.documentPartWithStatus,
        }}
        onItemClick={onItemClick}
        size={"small"}
      />
    </>
  );
};

export function getWorkflowStatus(
  itemId: string,
  documentPartWithStatuses: DocumentPartWithStatus[]
): WorkflowStatus | undefined {
  return documentPartWithStatuses?.find((item) => item.documentPartId === itemId)?.workflowStatus;
}
